import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Chip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaArrowLeft, FaArrowRight, FaPause, FaPlay, FaPenToSquare, FaLayerGroup, FaEye, FaBolt, FaWandMagicSparkles, FaGlobe, FaHeadphones, FaDownload, FaUpload } from 'react-icons/fa6';
import { autoDubWorkflowsState } from '../../store.jsx';
import { channel as findChannel, lang as findLang, STATUS } from '../../services/autoDub/mockData.js';
import { normalizeWorkflow, normalizeQueueItem } from '../../services/autoDub/mappers.js';
import repositories from '../../repositories.jsx';
import { useAutoDubChannels, useAutoDubWorkflows } from '../../hooks/useAutoDubData.jsx';
import Header from '../../components/Header.jsx';
import PageLoader from '../../components/PageLoader.jsx';
import PipelineStrip from '../../components/autoDub/PipelineStrip.jsx';
import QueueItem from '../../components/autoDub/QueueItem.jsx';
import ChannelAvatar from '../../components/autoDub/primitives/ChannelAvatar.jsx';
import ReconnectBanner from '../../components/autoDub/ReconnectBanner.jsx';
import { Pill } from '../../components/autoDub/primitives/bits.jsx';

function SettingLine({ icon, label }) {
	return <div className="flex items-center gap-2.5 text-[13px] text-slate-600"><span className="text-slate-400">{icon}</span>{label}</div>;
}

export default function AutoDubDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [workflows, setWorkflows] = useRecoilState(autoDubWorkflowsState);
	const { ready } = useAutoDubWorkflows();
	useAutoDubChannels();
	const wf = workflows.find(w => w.id === id);

	const [filter, setFilter] = useState('all');
	const [live, setLive] = useState(true);
	const [audioTrackModal, setAudioTrackModal] = useState(null);

	// fresh copy of this workflow (stats move while dubs run) merged into the atom
	const workflowQuery = useQuery({
		queryKey: ['autoDubWorkflow', id],
		queryFn: () => repositories.autoDub.workflows.get(id),
	});
	useEffect(() => {
		if (!workflowQuery.data) return;
		const fresh = normalizeWorkflow(workflowQuery.data);
		setWorkflows(ws => ws.some(w => w.id === fresh.id)
			? ws.map(w => (w.id === fresh.id ? fresh : w))
			: [fresh, ...ws]);
	}, [workflowQuery.data, setWorkflows]);

	// the live queue — polls while the Live toggle is on
	const queueQuery = useQuery({
		queryKey: ['autoDubQueue', id],
		queryFn: () => repositories.autoDub.queue(id).then(list => (list ?? []).map(q => normalizeQueueItem(q, wf))),
		enabled: !!wf,
		refetchInterval: live ? 5000 : false,
	});
	const items = queueQuery.data ?? [];

	const counts = useMemo(() => items.reduce((a, it) => { a[it.stage] = (a[it.stage] || 0) + 1; return a; }, {}), [items]);
	const filtered = filter === 'all' ? items : items.filter(it => it.stage === filter);

	const onApprove = (qid) => {
		const item = items.find(it => it.id === qid);
		// Audio-track dubs can't be auto-published (YouTube allowlist-only). Surface
		// the guidance in a modal with an action to finish in the editor instead.
		if (item?.mode === 'audio_track') {
			setAudioTrackModal({ projectId: item.projectId });
			return;
		}
		queryClient.setQueryData(['autoDubQueue', id], prev => (prev ?? []).map(it => it.id === qid ? { ...it, stage: 'publishing' } : it));
		repositories.autoDub.approve(id, qid)
			.catch(err => toast.error(err?.response?.data?.detail || 'Could not approve this dub. Please try again.'))
			.finally(() => queryClient.invalidateQueries({ queryKey: ['autoDubQueue', id] }));
	};
	const onRetry = (qid) => {
		queryClient.setQueryData(['autoDubQueue', id], prev => (prev ?? []).map(it => it.id === qid ? { ...it, stage: 'dubbing', progress: 5, error: null } : it));
		repositories.autoDub.retry(id, qid)
			.catch(err => toast.error(err?.response?.data?.detail || 'Could not retry this dub. Please try again.'))
			.finally(() => queryClient.invalidateQueries({ queryKey: ['autoDubQueue', id] }));
	};
	const onOpenEditor = (projectId) => navigate(`/app/${projectId}/edit`);

	if (!wf) {
		if (!ready) {
			return (
				<div className="relative">
					<Header currentPage="auto-dub" />
					<div className="py-16"><PageLoader /></div>
				</div>
			);
		}
		return (
			<div className="py-16 text-center">
				<p className="text-slate-500 mb-4">This workflow no longer exists.</p>
				<Button color="primary" variant="flat" onPress={() => navigate('/auto-dub')}>Back to Auto-Dub</Button>
			</div>
		);
	}

	const st = STATUS[wf.status] || STATUS.draft;
	const src = findChannel(wf.source);
	const dests = (wf.dests || []).map(findChannel).filter(Boolean);

	// Channels this workflow depends on (source + destinations), de-duped, that
	// have lost access — surfaces a reconnect banner so publishing doesn't silently break.
	const reconnectCount = [...new Set([wf.source, ...(wf.dests || [])].map(String))]
		.map(findChannel)
		.filter(c => c && (c.needs_reconnect || !c.connected))
		.length;
	const toggleStatus = () => {
		const prev = wf.status;
		const next = prev === 'active' ? 'paused' : 'active';
		setWorkflows(ws => ws.map(w => w.id === wf.id ? { ...w, status: next } : w));
		repositories.autoDub.workflows.update(wf.id, { status: next }).catch(() => {
			setWorkflows(ws => ws.map(w => w.id === wf.id ? { ...w, status: prev } : w));
			toast.error('Could not update the workflow. Please try again.');
		});
	};

	const filters = [['all', 'All'], ['dubbing', 'Dubbing'], ['review', 'Review'], ['published', 'Published'], ['failed', 'Failed']];

	return (
		<div className="relative">
			<Header currentPage="auto-dub" />
			<div className="py-6">
			{/* gradient hero */}
			<motion.div
				initial={{ opacity: 0, y: -12 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ type: 'spring', stiffness: 220, damping: 26 }}
				className={`relative overflow-hidden rounded-3xl px-6 py-5 md:px-7 md:py-6 mb-5 text-white bg-gradient-to-br ${wf.status === 'active' ? 'from-primary via-secondary to-fuchsia-600' : 'from-slate-500 via-slate-600 to-slate-700'}`}
			>
				<motion.span aria-hidden className="pointer-events-none absolute -top-20 -right-10 h-64 w-64 rounded-full bg-white/20 blur-3xl"
					animate={{ x: [0, -20, 0], y: [0, 16, 0] }} transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }} />
				<motion.span aria-hidden className={`pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full blur-3xl ${wf.status === 'active' ? 'bg-fuchsia-300/25' : 'bg-white/15'}`}
					animate={{ x: [0, 24, 0], y: [0, -14, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} />
				<span aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.12] bg-[linear-gradient(to_right,rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:26px_26px]" />

				<div className="relative">
					{/* back / breadcrumb */}
					<div className="flex items-center gap-2 mb-4">
						<Button isIconOnly radius="full" onPress={() => navigate('/auto-dub')} aria-label="Back"
							className="bg-white/15 text-white backdrop-blur hover:bg-white/25 min-w-8 w-8 h-8">
							<FaArrowLeft size={14} />
						</Button>
						<span className="text-[12px] font-semibold tracking-[0.04em] text-white/70">Auto-Dub workflows</span>
					</div>

					<div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
						<div className="min-w-0">
							<div className="flex items-center gap-2.5 flex-wrap mb-3.5">
								<h1 className="m-0 text-[24px] md:text-[27px] font-bold tracking-tight truncate">{wf.name}</h1>
								<span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-1 text-[12px] font-semibold">
									{wf.status === 'active'
										? <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-80" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" /></span>
										: <span className="inline-block h-2 w-2 rounded-full bg-white/70" />}
									{st.label}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-1 text-[12px] font-semibold">
									{wf.type === 'watch' ? <FaBolt size={10} /> : <FaLayerGroup size={10} />}
									{wf.type === 'watch' ? 'Watching uploads' : 'Backfilling'}
								</span>
							</div>

							{/* source → dest flow */}
							<div className={`flex items-center gap-2.5 flex-wrap ${wf.status === 'active' ? '' : 'opacity-80'}`}>
								<div className="rounded-[15px] ring-2 ring-white/50"><ChannelAvatar ch={src} size={40} /></div>
								<div className="relative flex items-center w-9">
									<span className="flex-1 border-t-2 border-dashed border-white/40" />
									<FaArrowRight size={12} className="text-white/70 -ml-1" />
									{wf.status === 'active' && (
										<motion.span className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-white shadow"
											animate={{ left: ['0%', '85%'], opacity: [0, 1, 0] }}
											transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }} />
									)}
								</div>
								<div className="flex">
									{wf.publishMode === 'audio_track'
										? <span className="inline-flex items-center gap-1.5 rounded-[14px] bg-white/15 backdrop-blur px-3 h-10 text-[12px] font-semibold ring-2 ring-white/30"><FaLayerGroup size={12} /> Audio track</span>
										: <>
											{dests.slice(0, 3).map((d, i) => (
												<div key={d.id} className="rounded-[14px]" style={{ marginLeft: i ? -12 : 0, border: '2.5px solid rgba(255,255,255,.85)', zIndex: 3 - i }}>
													<ChannelAvatar ch={d} size={38} />
												</div>
											))}
											{dests.length > 3 && (
												<div className="grid place-items-center text-[12px] font-bold text-white bg-white/25 rounded-[13px]" style={{ marginLeft: -12, width: 38, height: 38, border: '2.5px solid rgba(255,255,255,.85)' }}>+{dests.length - 3}</div>
											)}
										</>}
								</div>
								<span className="inline-flex items-center text-white/70 ml-1"><FaGlobe size={13} /></span>
								{wf.langs.map(c => {
									const l = findLang(c);
									return (
										<span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-1 text-[12px] font-semibold">
											{l.name}
										</span>
									);
								})}
							</div>
						</div>

						<div className="flex gap-2 flex-none">
							<Button radius="full" onPress={toggleStatus}
								className="bg-white text-primary font-semibold shadow-lg shadow-black/10 hover:scale-[1.02] transition-transform"
								startContent={wf.status === 'active' ? <FaPause size={13} /> : <FaPlay size={13} />}>
								{wf.status === 'active' ? 'Pause' : 'Resume'}
							</Button>
							<Button radius="full" onPress={() => navigate(`/auto-dub/${id}/edit`)}
								className="bg-white/15 text-white backdrop-blur hover:bg-white/25 font-semibold"
								startContent={<FaPenToSquare size={13} />}>
								Edit
							</Button>
						</div>
					</div>
				</div>
			</motion.div>

			{reconnectCount > 0 && (
				<ReconnectBanner count={reconnectCount} onGoToChannels={() => navigate('/channels')} />
			)}

			<div className="grid gap-5 items-start" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px' }}>
				{/* main */}
				<motion.div className="flex flex-col gap-4"
					initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06, type: 'spring', stiffness: 240, damping: 26 }}>
					<PipelineStrip counts={counts} />

					<div className="flex items-center justify-between">
						<div className="flex gap-1.5 bg-slate-100 rounded-xl p-1">
							{filters.map(([k, lbl]) => {
								const on = filter === k;
								return (
									<button key={k} type="button" onClick={() => setFilter(k)}
										className={`px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition ${on ? 'bg-white text-slate-800 shadow-small' : 'text-slate-500'}`}>
										{lbl}{k !== 'all' && counts[k] ? ` ${counts[k]}` : ''}
									</button>
								);
							})}
						</div>
						<button type="button" onClick={() => setLive(l => !l)} className={`flex items-center gap-2 text-[13px] font-semibold ${live ? 'text-emerald-500' : 'text-slate-400'}`}>
							<span className="w-2 h-2 rounded-full" style={{ background: live ? '#16c098' : '#9aa2b4', boxShadow: live ? '0 0 0 4px rgba(22,192,152,.18)' : 'none' }} />
							{live ? 'Live' : 'Paused'}
						</button>
					</div>

					<div className="flex flex-col gap-3">
						{filtered.length === 0
							? <div className="bg-white rounded-3xl border border-slate-200 shadow-small p-10 text-center text-slate-500 text-sm">Nothing in this stage right now.</div>
							: filtered.map(it => <QueueItem key={it.id} it={it} onApprove={onApprove} onRetry={onRetry} onReconnect={() => navigate('/channels')} onOpenEditor={onOpenEditor} />)}
					</div>
				</motion.div>

				{/* sidebar */}
				<motion.div className="flex flex-col gap-4 sticky top-2"
					initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, type: 'spring', stiffness: 240, damping: 26 }}>
					<div className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_10px_40px_-28px_rgba(15,23,42,0.35)] overflow-hidden">
						<div className="p-5">
						<div className="flex items-center gap-2 mb-3.5">
							<span className="h-3.5 w-1 rounded-full bg-gradient-to-b from-primary to-fuchsia-500" />
							<span className="text-[12px] font-bold tracking-[0.06em] uppercase text-slate-500">Pipeline</span>
						</div>
						<div className="flex items-center gap-2.5 mb-3.5">
							<ChannelAvatar ch={src} size={42} />
							<div className="min-w-0">
								<div className="text-[13.5px] font-semibold text-slate-800">{src?.name}</div>
								<div className="text-[12px] text-slate-400">Source · {src?.handle}</div>
							</div>
						</div>
						<div className="flex flex-col gap-2 pl-1.5 border-l-2 border-dashed border-slate-200 ml-3.5 pb-1">
							{wf.langs.map(c => { const l = findLang(c); return <Pill key={c} className="self-start">{l.name}</Pill>; })}
						</div>
						<div className="flex flex-col gap-2 mt-3">
							{wf.publishMode === 'audio_track'
								? <Chip size="sm" variant="flat" color="secondary" startContent={<span className="ml-1"><FaLayerGroup size={11} /></span>}>Audio track on original</Chip>
								: dests.map(d => (
									<div key={d.id} className="flex items-center gap-2.5">
										<ChannelAvatar ch={d} size={36} />
										<div><div className="text-[13px] font-semibold text-slate-800">{d.name}</div><div className="text-[11.5px] text-slate-400">{d.handle}</div></div>
									</div>
								))}
						</div>
						</div>

					<div className="border-t border-slate-100 p-5">
						<div className="flex items-center gap-2 mb-3">
							<span className="h-3.5 w-1 rounded-full bg-gradient-to-b from-primary to-fuchsia-500" />
							<span className="text-[12px] font-bold tracking-[0.06em] uppercase text-slate-500">Settings</span>
						</div>
						<div className="flex flex-col gap-3">
							<SettingLine icon={wf.review ? <FaEye size={15} /> : <FaBolt size={15} />} label={wf.review ? 'Review before publish' : 'Auto-publish'} />
							<SettingLine icon={<FaEye size={15} />} label={`Visibility · ${wf.privacy}`} />
							<SettingLine icon={<FaWandMagicSparkles size={15} />} label={wf.voice === 'clone' ? 'Voice · cloned' : 'Voice · fixed'} />
							<SettingLine icon={<FaGlobe size={15} />} label={`${wf.langs.length} language${wf.langs.length > 1 ? 's' : ''}`} />
						</div>
						<Button variant="bordered" size="sm" radius="full" className="w-full mt-3.5" startContent={<FaPenToSquare size={13} />} onPress={() => navigate(`/auto-dub/${id}/edit`)}>Edit workflow</Button>
						</div>

					<div className="border-t border-slate-100 p-5">
						<div className="flex items-center gap-2 mb-2.5">
							<span className="h-3.5 w-1 rounded-full bg-gradient-to-b from-primary to-fuchsia-500" />
							<span className="text-[12px] font-bold tracking-[0.06em] uppercase text-slate-500">Credits this month</span>
						</div>
						{(() => {
							const pct = Math.min(100, Math.round(wf.creditsMonth / wf.capMonth * 100));
							return (
								<>
									<div className="flex items-baseline gap-1.5 mb-2">
										<span className="text-2xl font-bold text-slate-800">{wf.creditsMonth.toLocaleString()}</span>
										<span className="text-[12px] text-slate-400">/ {wf.capMonth.toLocaleString()} cap</span>
									</div>
									<div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
										<motion.div className="h-full rounded-full"
											initial={{ width: 0 }} animate={{ width: pct + '%' }}
											transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
											style={{ background: pct > 85 ? '#f23e5c' : 'linear-gradient(90deg,#5A0AE0,#c026d3)' }} />
									</div>
									<div className="text-[12px] text-slate-400 mt-2">Pauses automatically when the cap is reached.</div>
								</>
							);
						})()}
						</div>
					</div>
				</motion.div>
			</div>
			</div>

			<Modal isOpen={!!audioTrackModal} onClose={() => setAudioTrackModal(null)} size="md" placement="center" hideCloseButton classNames={{ base: 'rounded-3xl overflow-hidden' }}>
				<ModalContent>
					{/* gradient header with the headphones mark */}
					<div className="relative overflow-hidden bg-gradient-to-br from-[#8C2CE2] to-[#5A0AE0] px-6 pb-7 pt-6 text-white">
						<span aria-hidden className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
						<span aria-hidden className="pointer-events-none absolute -bottom-12 left-10 h-32 w-32 rounded-full bg-fuchsia-300/30 blur-2xl" />
						<div className="relative flex items-center gap-3.5">
							<div className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-white/15 ring-1 ring-white/30 backdrop-blur"><FaHeadphones size={20} /></div>
							<div>
								<h3 className="text-[17px] font-semibold leading-tight">Finish this audio-track dub</h3>
								<p className="mt-0.5 text-[12.5px] text-white/80">A quick 3-step handoff to YouTube Studio</p>
							</div>
						</div>
					</div>

					<ModalBody className="px-6 py-5">
						<p className="text-[13.5px] leading-relaxed text-slate-600">
							We’re not able to auto-upload the audio track. Open the dub in the editor, export it, then upload the track in YouTube Studio.
						</p>

						<div className="mt-2 flex flex-col gap-2.5">
							{[
								{ icon: <FaPenToSquare size={14} />, title: 'Open in the editor', desc: 'Review and fine-tune the dubbed track.' },
								{ icon: <FaDownload size={14} />, title: 'Export the track', desc: 'Download the finished audio file.' },
								{ icon: <FaUpload size={14} />, title: 'Upload in YouTube Studio', desc: 'Add it as an alternate audio track.' },
							].map((s, i) => (
								<div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-slate-50/60 px-3.5 py-2.5">
									<span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-secondary/10 text-secondary">{s.icon}</span>
									<div className="min-w-0 flex-1">
										<p className="text-[13px] font-semibold text-slate-800">{s.title}</p>
										<p className="text-[12px] text-slate-500">{s.desc}</p>
									</div>
									<span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-white text-[11px] font-bold text-secondary ring-1 ring-secondary/20">{i + 1}</span>
								</div>
							))}
						</div>
					</ModalBody>

					<ModalFooter>
						<Button variant="bordered" radius="full" onPress={() => setAudioTrackModal(null)}>Close</Button>
						<Button
							color="primary"
							radius="full"
							isDisabled={!audioTrackModal?.projectId}
							startContent={<FaPenToSquare size={13} />}
							onPress={() => { const pid = audioTrackModal?.projectId; setAudioTrackModal(null); if (pid) navigate(`/app/${pid}/edit`); }}
						>
							Go to editor &amp; export
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</div>
	);
}
